<template>
  <div>
    <div id="my-container">
      <b-navbar id="header" type="light" variant="light" sticky>
        <b-navbar-nav>
          <h4 class="mb-0">{{title}}</h4>
        </b-navbar-nav>

        <b-navbar-nav class="ml-auto">
          <b-row>
            <b-col>
              <b-input-group size="sm">
                <b-button size="sm" class="mr-1" :disabled="selected.length === 0 || isBusyExport" variant="info" @click="exportTimesheetFile()">
                  <b-spinner v-if="isBusyExport" small/>
                  <font-awesome-icon icon="table" class="mr-1"/>
                  Скачать Табель
                </b-button>
                <export-sidebar
                  sidebar-id="sidebar-tasks-export"
                  storage-key="sidebar_tasks_export"
                  :columns="exportableFields"
                  :data-provider="provideExportData"
                  :data-mapper="mapTaskForExport"
                  file-name="Tasks.xlsx"
                  sheet-name="Tasks"
                  :is-busy="isBusy"
                  :items-count="totalRows"
                >
                  <template #extra-buttons>
                    <b-button size="sm" variant="warning" @click="toggleTW()" class="ml-1">
                      <font-awesome-icon icon="info" class="mr-1"/>
                      {{ isIncludeTW ? 'Скрыть детализацию' : 'Детализация' }}
                    </b-button>
                  </template>
                </export-sidebar>
                <b-button size="sm" class="mr-2" v-if="visibleAdmins()" :disabled="isBusyExport" variant="info" @click="showImportModal()">
                  <b-spinner v-if="isBusyExport" small/>
                  <font-awesome-icon icon="file-download" class="mr-1"/>
                  Импорт
                </b-button>
              </b-input-group>
            </b-col>
            <b-col cols="auto">
              <b-button id="table-settings" size="sm" variant="warning" class="mr-2" v-b-toggle.sidebar-fields>
                <font-awesome-icon icon="cog"/>
              </b-button>
              <b-tooltip target="table-settings" triggers="hover" variant="warning" noninteractive>
                Колонки
              </b-tooltip>
              <b-button v-if="canEditTasks()" size="sm" variant="success" :disabled="isBusy" @click="onCreate()">
                <b-spinner v-if="isBusy" small/>
                <font-awesome-icon icon="plus" class="mr-1"/>
                Новая
              </b-button>
            </b-col>
          </b-row>
        </b-navbar-nav>
      </b-navbar>

      <filters-drawer
        :query.sync="filters.query"
        :is-busy="isBusy"
        :filter-tags="activeFilterTags"
        @remove-tag="removeFilterTag"
        @submit="onSearch"
        @clear="onClearFilters"
      >
        <b-col cols="12">
          <label class="small text-muted">Период</label>
          <b-row class="align-items-center">
            <b-col>
              <b-form-input
                id="input-start-date"
                size="sm"
                type="date"
                v-model="filters.start_date"
              />
            </b-col>
            <b-col cols="auto" class="px-2">
              <span>&mdash;</span>
            </b-col>
            <b-col>
              <b-form-input
                id="input-end-date"
                size="sm"
                type="date"
                v-model="filters.end_date"
              />
            </b-col>
          </b-row>
        </b-col>
        <b-col cols="12" class="mt-1">
          <label class="small text-muted">Объекты</label>
          <location-multi-select
            id="location-multi-select"
            ref="location_multi_select"
            v-on:onSelect="onSelectLocation"
            :start-date="filters.start_date"
            :end-date="filters.end_date"
            :show-date-button="true"
          />
        </b-col>
        <b-col v-if="visibleAdmins()" cols="12" class="mt-1">
          <label class="small text-muted">Сотрудники</label>
          <user-multi-select
            id="user-multi-select"
            ref="user_multi_select"
            v-on:onSelect="onSelectUser"
          />
        </b-col>
        <b-col md="6" cols="12" class="mt-1">
          <label class="small text-muted">Статус заявки</label>
          <b-form-select
            id="input-statuses"
            size="sm"
            v-model="filters.status_id"
          >
            <option :value="0">Все статусы</option>
            <option v-for="option in activeStatuses" :key="option.id" :value="option.id">
              {{ option.name }}
            </option>
          </b-form-select>
        </b-col>
        <b-col md="6" cols="12" class="mt-1">
          <label class="small text-muted">Статус оплаты</label>
          <b-form-select
            id="input-paid-workers-statuses"
            size="sm"
            v-model="filters.paid_workers"
          >
            <option :value="0">Все статусы оплаты</option>
            <option v-for="option in activePaidWorkersStatuses" :key="option.id" :value="option.id">
              {{ option.name }}
            </option>
          </b-form-select>
        </b-col>
      </filters-drawer>

      <b-table
        id="tasks"
        ref="tasks"
        class="mb-0 text-small"
        :style="tableStyle"
        select-mode="multi"
        selectedVariant="success"
        striped
        small
        responsive
        show-empty
        :empty-text="$t('message.common.emptyTable')"
        :busy="isBusy"
        :items="items"
        :fields="renderedFields"
        no-provider-filtering
        no-provider-paging
        no-provider-sorting
        no-local-sorting
        @sort-changed="sortChanged"
        @row-dblclicked="onRowClicked"
        @row-clicked="rowClicked"
        :tbody-tr-class="tbodyRowClass"
        sticky-header
        head-variant="light"
      >
        <template #table-busy>
          <div class="text-center text-success my-2">
            <b-spinner class="align-middle"/>
            <strong>Загрузка</strong>
          </div>
        </template>

        <template #head(selected)="row">
          <b-form-checkbox
            class="mx-1"
            v-model="allSelected"
            :indeterminate="indeterminate"
            :disabled = "allSelectDisabled"
            size="md"
            @change="selectAllRows"
          />
        </template>

        <template #cell(selected)="row">
          <template v-if="row.item.selected">
            <span aria-hidden="true">&check;</span>
            <span class="sr-only">Выбрана</span>
          </template>
          <template v-else>
            <span >&nbsp;</span>
            <span class="sr-only">Не выбрана</span>
          </template>
        </template>

        <template #cell(action)="row">
          <b-dropdown size="sm" no-caret>
            <template #button-content>
              <font-awesome-icon :icon="['fas', 'ellipsis-v']"/>
            </template>
            <b-dropdown-item-button variant="success" @click.stop="onRead(row.item)" target="_blank">
              <font-awesome-icon icon="eye"/>
              Смотреть
            </b-dropdown-item-button>
            <b-dropdown-item-button v-if="isVisibleReadOnlyForRow(row.item)" variant="primary" @click="onEdit(row.item)">
              <font-awesome-icon icon="edit"/>
              Редактировать
            </b-dropdown-item-button>
            <b-dropdown-item-button variant="warning" @click="onCopy(row.item)">
              <font-awesome-icon icon="copy"/>
              Копировать
            </b-dropdown-item-button>
            <b-dropdown-item-button v-if="row.item.invoice_numb" variant="info" @click="onInvoiceFile(row.item)">
              <font-awesome-icon icon="copy"/>
              Скачать счёт
            </b-dropdown-item-button>
            <b-dropdown-item-button v-if="isVisibleReadOnlyForRow(row.item)" variant="danger" @click="onRemove(row)">
              <font-awesome-icon :icon="['fas', 'trash']"/>
              Удалить
            </b-dropdown-item-button>
          </b-dropdown>
        </template>

        <template #cell(location_name)="row">
          {{row.item.location_name}}
          <p class="m-0 p-0 text-secondary text-small">{{row.item.customer_name}}</p>
        </template>

        <template #cell(payment_type_int)="row">
          <font-awesome-icon :set="pt = paymentTypeIcon(row.item.payment_type_int)" :icon="['fas', pt.icon]" :title="pt.title"/>
        </template>

        <template #cell(start_date)="row">
          {{formatTableDateTime(row.item.start_date)}}
        </template>

        <template #cell(task_workers)="row">
          {{len(row.item.task_workers)}}
        </template>

        <template #cell(paid_workers)="row">
          <font-awesome-icon :icon="['fas', paidWorkers(row).icon]" :style=paidWorkers(row).style />
        </template>

        <template #cell(status_id)="row">
          <div class="d-flex justify-content-between align-items-center">
            <span style="font-size: medium">
              <b-badge pill :variant="getStatusStyle(row.item.status_id)">
                {{getStatusName(row.item.status_id)}}
              </b-badge>
            </span>
            <span>
              <font-awesome-icon
                :icon="['fas', paidWorkers(row).icon]"
                :style=paidWorkers(row).style
                v-b-tooltip.hover.top title="Оплата исполнителям"
              />
              <font-awesome-icon
                :icon="['fas', readOnly(row).icon]"
                :style=readOnly(row).style
                v-b-tooltip.hover.top title="Редактирование заявки"
              />
            </span>
          </div>
        </template>

        <template #cell(invoice_numb)="row">
          <b-button v-if="visibleAdmins() && row.item.invoice_numb" size="sm" variant="link" @click.stop="showInvoice(row.item)">
            {{row.item.invoice_numb}}
          </b-button>
          <div v-else>
            {{row.item.invoice_numb}}
          </div>
        </template>

        <template #cell(executor_name)="row">
          {{row.item.executor_name}}
          <p class="m-0 p-0 text-small">{{row.item.total_manager}} ₽</p>
        </template>

        <template #cell(location_rate)="row">
          {{row.item.payment_first}} ₽
          <p class="m-0 p-0 text-small">{{ getUnit(row.item.unit_code) }}</p>
        </template>

        <template #cell(read_only)="row">
          <font-awesome-icon :icon="['fas', readOnly(row).icon]" :style=readOnly(row).style />
        </template>
      </b-table>

      <pagination-footer
        :total-rows="totalRows"
        :per-page.sync="perPage"
        @page-click="onPagination"
        @per-page-change="onPerPageChange"
      >
        <template #footer-update>
          <b-row v-if="selected.length > 0" align-h="between" align-v="center">
            <b-col cols="auto" class="mr-auto text-small">
              Выбрано: <b>{{selected.length}}</b>
              | С заказчика: <b>{{totalSelectedPrice}}</b>
              | Кол-во исп: <b>{{totalSelectedWorkers}}</b>
              | Кол-во: <b>{{totalSelectedUnits}}</b>
              | Исполнителям: <b>{{totalSelectedWorkerPrice}}</b>
              | Менеджеру: <b>{{totalSelectedManager}}</b>
              | Руководителю: <b>{{totalSelectedProject}}</b>
            </b-col>
            <b-col cols="auto">
              <b-button v-if="visibleAdmins()" size="sm" variant="info" :disabled="!allStatusesEqual || newStatusDisabled" @click="onNewStatus()">
                <font-awesome-icon icon="undo" class="mr-1"/>
                На доработку
              </b-button>
              <b-button size="sm" variant="info" :disabled="!allStatusesEqual || payStatusDisabled" @click="onPayStatus()">
                <font-awesome-icon icon="cash-register" class="mr-1"/>
                На оплату
              </b-button>

              <b-button v-if="visibleAdmins()" size="sm" variant="warning" :disabled="payWorkersDisabled" @click="onPayWorkers()">
                <font-awesome-icon icon="receipt" class="mr-1"/>
                Оплатить исполнителям
              </b-button>

              <b-button v-if="visibleAdmins()" size="sm" variant="info" :disabled="!allStatusesEqual || invoiceDisabled" @click="onInvoice()">
                <font-awesome-icon icon="file-invoice-dollar" class="mr-1"/>
                Выставить счёт
              </b-button>
            </b-col>
          </b-row>
          <hr v-if="selected.length > 0"/>
        </template>
      </pagination-footer>
    </div>

    <tasks-import ref="tasks_import" @refresh="handleTasksImport"/>

    <dynamic-tables
      storage-key="tasks_columns"
      sidebar-fields="sidebar-fields"
      :fields-model.sync="fields"
      fieldKey="id"
    />

    <b-modal
      id="modal-select-pay-organization"
      ref="modalSelectPayOrganization"
      title="Организация"
      @ok="onPay()"
      ok-title="Оплатить"
      button-size="sm"
      :ok-disabled="this.currentPayOrganization == null"
      :cancel-title="$t('message.actions.cancel')"
    >
      <organization-select
        id="input-org-select"
        ref="organization_select"
        v-bind:initial-id="0"
        v-on:onSelect="onSelectOrg"
      />
    </b-modal>
  </div>
</template>

<script>
import tasks from "../../network/tasks";
import users from "../../network/users";
import invoices from "../../network/invoices";
import balansPro from "../../network/balansPro";
  import dateFormatter from '../../utilites/dateFormatter';
  import helpers from "../../utilites/helpers";
  import permissions, {PERM} from "../../utilites/permissions";
  import FiltersDrawer from "../../utilites/commonUi/FiltersDrawer.vue";
  import ExportSidebar from "../../utilites/commonUi/ExportSidebar.vue";
  import unitsStore from "../../utilites/unitStore";
  import DynamicTables from "../../utilites/commonUi/DynamicTables.vue";
  import PaginationFooter from "../../utilites/commonUi/PaginationFooter.vue";
  import pageSearchService from "../../utilites/pageSearchService";
    import TasksImport from "./TasksImport";
    import LocationMultiSelect from "../Locations/LocationMultiSelect";
    import UserMultiSelect from "../Users/UserMultiSelect";
    import {parse} from "content-disposition-attachment";
    import OrganizationSelect from "../Organizations/OrganizationSelect";

export default {
  name: 'Tasks',
  components: { PaginationFooter, ExportSidebar, FiltersDrawer, DynamicTables, TasksImport, LocationMultiSelect, UserMultiSelect, OrganizationSelect },
  mixins: [
    tasks, users, invoices, balansPro,
    dateFormatter, helpers, unitsStore, permissions, pageSearchService
  ],
  data() {
    return {
      title: 'Заявки',
      isBusy: false,
      isBusyExport: false,
      items: [],
      serviceStartField: {
        key: 'selected',
        label: '',
        class:'tasksSelect'
      },
      fields: [
        {
          key: 'id',
          label: 'id',
          class:'tasksId',
          sortable: true,
          visible: true,
          exportable: true,
        },
        {
          key: 'location_name',
          label: this.$t('message.tasks.table.location_name'),
          class:'tasksLocation',
          sortable: true,
          visible: true,
          exportable: true,
        },
        {
          key: 'start_date',
          label: 'Дата',
          class:'tasksDate',
          sortable: true,
          visible: true,
          exportable: true,
        },
        {
          key: 'workers_count',
          label: this.$t('message.tasks.table.workers_count'),
          class:'tasksWorkers',
          sortable: true,
          visible: true,
          exportable: true,
        },
        {
          key: 'total_hours',
          label: 'Кол-во ед.',
          class:'tasksTotalHours',
          sortable: true,
          visible: true
        },
        {
          key: 'total_worker_price_with_comiss',
          label: 'Исполнителям ₽',
          class:'tasksTotalWorkerPrice',
          sortable: true,
          visible: true,
          exportable: true,
        },
        {
          key: 'total_price',
          label: 'C заказчика ₽',
          class:'tasksTotalPrice',
          sortable: true,
          visible: true,
          exportable: true,
        },
        {
          key: 'invoice_numb',
          label: 'Счёт',
          class:'tasksInvoice',
          sortable: true,
          visible: true
        },
        {
          key: 'status_id',
          label: 'Статус',
          class:'tasksStatus',
          sortable: true,
          visible: true
        },
        {
          key: 'executor_name',
          label: 'Менеджер',
          class:'tasksExecutor',
          sortable: true,
          visible: true,
          exportable: true,
        },
        {
          key: 'location_rate',
          label: 'Ставка объекта',
          class:'tasksLocationRate',
          sortable: true,
          visible: true
        },
      ],
      serviceEndField: {
        key: 'action',
        label: this.$t('message.tasks.table.actions'),
        class:'tasksAction'
      },
      additionalExportFields: [
        {
          key: 'customer_name',
          label: "Заказчик",
          visible: true,
          exportable: true,
        },
        {
          key: 'start_address',
          label: "Адрес заявки",
          visible: true,
          exportable: true,
        },
        {
          key: 'end_date',
          label: 'Дата окончания',
          visible: true,
          exportable: true,
        },
        {
          key: 'payment_first',
          label: 'Ставка вида работ',
          visible: true,
          exportable: true,
        },
        {
          key: 'author_name',
          label: 'Автор',
          visible: true,
          exportable: true,
        },
        {
          key: 'create_date',
          label: 'Дата создания',
          visible: true,
          exportable: true,
        },
        {
          key: 'additional_cost',
          label: 'Добавленные расходы',
          visible: true,
          exportable: true,
        },
        {
          key: 'declarated_cost',
          label: 'Заявленные расходы',
          visible: true,
          exportable: true,
        },
        {
          key: 'cargo_description',
          label: 'Примечание',
          visible: true,
          exportable: true,
        },
        {
          key: 'status_name',
          label: 'Статус',
          visible: true,
          exportable: true,
        },
        {
          key: 'tw_worker_name',
          label: 'Исп. Имя',
          visible: false,
          exportable: true,
          isTW: true,
        },
        {
          key: 'tw_worker_phone',
          label: 'Исп. Телефон',
          visible: false,
          exportable: true,
          isTW: true,
        },
        {
          key: 'tw_worker_inn',
          label: 'Исп. ИНН',
          visible: false,
          exportable: true,
          isTW: true,
        },
        {
          key: 'tw_worker_card',
          label: 'Исп. Карта',
          visible: false,
          exportable: true,
          isTW: true,
        },
        {
          key: 'tw_price',
          label: 'Исп. Ставка',
          visible: false,
          exportable: true,
          isTW: true,
        },
        {
          key: 'tw_start_date',
          label: 'Исп. Дата начала',
          visible: false,
          exportable: true,
          isTW: true,
        },
        {
          key: 'tw_end_date',
          label: 'Исп. Дата окончания',
          visible: false,
          exportable: true,
          isTW: true,
        },
        {
          key: 'tw_dinner',
          label: 'Исп. Обед',
          visible: false,
          exportable: true,
          isTW: true,
        },
        {
          key: 'tw_total_units',
          label: 'Исп. Количество',
          visible: false,
          exportable: true,
          isTW: true,
        },
        {
          key: 'tw_prepaid',
          label: 'Исп. Аванс',
          visible: false,
          exportable: true,
          isTW: true,
        },
        {
          key: 'tw_road_price',
          label: 'Исп. Трансфер',
          visible: false,
          exportable: true,
          isTW: true,
        },
        {
          key: 'tw_total_price',
          label: 'Исп. Итого',
          visible: false,
          exportable: true,
          isTW: true,
        },
        {
          key: 'tw_description',
          label: 'Исп. Примечание',
          visible: false,
          exportable: true,
          isTW: true,
        },
      ],
      isIncludeTW: false,
      sortBy: 'id',
      sortDesc: true,
      filters: this.getDefaultFilters(),
      deleteRow: {
        id: null,
        start_address: ''
      },
      executors: [],
      selected: [],
      allSelected: false,
      indeterminate: false,
      allSelectDisabled: true,
      tableStyle: {
        maxHeight: 'calc(100vh - 218.8px) !important',
        contentVisibility: 'auto'
      },
      statuses: [],
      paidStatuses: [
        {id: 1, name: 'Не оплачено'},
        {id: 2, name: 'Оплачено'},
      ],
      currentPayOrganization: null,
      currentPayTaskData: null,
      isFiltersOpen: false,
    }
  },
  async created() {
    this.isBusy = true;
    const query = this.$route.query;

    if (query) {
      if (query.query) {
        this.filters.query = query.query;
      }

      if (query.start_date) {
        this.filters.start_date = query.start_date;
      } else {
        this.filters.start_date = this.getStartDateFilter();
      }

      if (query.end_date) {
        this.filters.end_date = query.end_date;
      }

      if (query.status_id) {
        this.filters.status_id = query.status_id;
      }

      if (query.paid_workers) {
        this.filters.paid_workers = query.paid_workers;
      }

      if (query.locations) {
        const decodeLoc = decodeURIComponent(query.locations);
        this.filters.locations = decodeLoc.split(",");
      }

      if (query.users) {
        const decodeLoc = decodeURIComponent(query.users);
        this.filters.users = decodeLoc.split(",");
      }
    } else {
      this.filters.start_date = this.getStartDateFilter();
      this.filters.end_date = null;
    }

    if (this.$route.hash) {
      this.currentPage = this.getPage(this.$route.hash);
    }

    await this.getExecutors();

    this.statuses = (await this.getTaskStatuses()).data.items;

    await this.onSearch();
  },
  computed: {
    allStatusesEqual() {
      const taskStatuses = this.selected.map(obj => obj.status_id);
      const allEqual = arr => arr.every(v => v === arr[0]);
      return allEqual(taskStatuses);
    },
    newStatusDisabled() {
      const taskStatuses = this.selected.map(obj => obj.status_id);
      const allEqual = arr => arr.every(v => v !== 2);

      const taskWorkerPaid = this.selected.map(obj => obj.paid_workers);
      const paid = taskWorkerPaid.find(x => x === true);

      return allEqual(taskStatuses) || paid != null;
    },
    payStatusDisabled() {
      const taskStatuses = this.selected.map(obj => obj.status_id);
      const allEqual = arr => arr.every(v => v !== 1);
      return allEqual(taskStatuses);
    },
    payWorkersDisabled() {
      const taskStatuses = this.selected.map(obj => obj.status_id);
      const status = taskStatuses.find(x => x === 1);

      const taskWorkerPaid = this.selected.map(obj => obj.paid_workers);
      const paid = taskWorkerPaid.find(x => x === true);

      return paid != null || status != null;
    },
    invoiceDisabled() {
      const taskStatuses = this.selected.map(obj => obj.status_id);
      const allEqual = arr => arr.every(v => v !== 2);
      return allEqual(taskStatuses);
    },
    activeStatuses() {
      return this.statuses;
    },
    activePaidWorkersStatuses() {
      return this.paidStatuses;
    },
    totalSelectedPrice() {
      return this.priceFormat(
        this.selected
        .map(obj => obj.total_price)
        .reduce((acc, current) => acc + current, 0),true
      );
    },
    totalSelectedWorkerPrice() {
      return this.priceFormat(
        this.selected
        .map(obj => obj.total_worker_price)
        .reduce((acc, current) => acc + current, 0),true
      );
    },
    totalSelectedUnits() {
      return this.formatStatsUnitTotals(this.selected);
    },
    totalSelectedManager() {
      return this.priceFormat(
        this.selected
        .map(obj => obj.total_manager)
        .reduce((acc, current) => acc + current, 0), true
      );
    },
    totalSelectedProject() {
      return this.priceFormat(
        this.selected
          .map(obj => obj.total_project ? obj.total_project : 0)
          .reduce((acc, current) => acc + current, 0), true
      );
    },
    totalSelectedWorkers() {
      return this.selected
        .map(obj => obj.workers_count)
        .reduce((acc, current) => acc + current, 0)
    },
    visibleFields() {
      return this.fields.filter(field => field.visible);
    },
    renderedFields() {
      return [
        this.serviceStartField,
        ...this.visibleFields,
        this.serviceEndField
      ];
    },
    exportableFields() {
      let allFields = [...this.fields, ...this.additionalExportFields]
        .filter(field => field.exportable);

      if (!this.isIncludeTW) {
        allFields = allFields.filter(field => !field.isTW);
      }

      return allFields.map(field => ({
        key: field.key,
        label: field.label,
        visible: field.visible,
      }));
    },
    activeFilterTags() {
      const tags = [];
      const defaults = this.getDefaultFilters();
      const start = this.filters.start_date;
      const end = this.filters.end_date;

      if (start || end) {
        let dateTag = '';

        if (start && end) {
          dateTag = `${this.formatTableDate(start)} - ${this.formatTableDate(end)}`;
        } else if (start) {
          dateTag = `с ${this.formatTableDate(start)}`;
        } else {
          dateTag = `по ${this.formatTableDate(end)}`;
        }
        tags.push({ key: 'date', label: dateTag });
      }

      if (this.filters.locations.length > 0) {
        tags.push({ key: 'location', label: `Объекты: ${this.filters.locations.length}`});
      }

      if (this.filters.users.length > 0) {
        tags.push({ key: 'user', label: `Сотрудники: ${this.filters.users.length}`,});
      }

      if (this.filters.status_id !== defaults.status_id) {
        const foundItem = this.activeStatuses.find(s => s.id === this.filters.status_id);
        tags.push({ key: 'status', label: foundItem ? foundItem.name : 'Статус заявки' });
      }

      if (this.filters.paid_workers !== defaults.paid_workers) {
        const foundItem = this.paidStatuses.find(s => s.id === this.filters.paid_workers);
        tags.push({ key: 'paid', label: foundItem ? foundItem.name : 'Статус оплаты' });
      }

      return tags;
    },
  },
  async mounted() {
    if (this.$refs.location_multi_select) {
      await this.$refs.location_multi_select.configure(this.filters.locations);
    }

    if (this.$refs.user_multi_select) {
      await this.$refs.user_multi_select.configure(this.filters.users);
    }

    const tableEl = this.$refs.tasks.$el
    const offsetLeft = tableEl.getBoundingClientRect().left
    const stickColEl = document.querySelectorAll('.sticky-column')
    stickColEl.forEach((el) => {
      const { x } = el.getBoundingClientRect()
      el.style.left = `${x - offsetLeft}px`
    })
  },
  methods: {
    async getExecutors() {
      const result = await this.getUsers();

      if (!result || !result.data) {
        this.executors = [];
        return;
      }

      this.executors = result.data.users;
    },
    visibleAdmins() {
      return this.hasPermission([PERM.TASK_EDIT_OTHER]);
    },
    async getItems() {
      const locationIds = this.filters.locations.map(function (item) {
        return "&locations[]=" + item;
      });

      const usersIds = this.filters.users.map(function (item) {
        return "&executors[]=" + item;
      });

      const data = {
        page: this.currentPage,
        size: this.perPage,
        query: this.filters.query,
        start_date: this.filters.start_date,
        end_date: this.filters.end_date,
        status_id: this.filters.status_id,
        paid_workers: this.filters.paid_workers,
        locations: locationIds.join(''),
        users: usersIds.join(''),
        sortBy: [this.sortBy],
        sortDesc: [this.sortDesc],
        include_tw: false,
      }

      this.isBusy = true;
      const result = await this.getTasks(data);

      if (!result || !result.data) {
        this.items = [];
        this.totalRows = 0;
        this.isBusy = false;
        this.showError("Что-то пошло не так. Попробуйте обновить страницу и повторить действие.");
        return;
      }

      this.items = result.data.tasks;
      this.totalRows = result.data.total;
      this.updateSelected();
      this.isBusy = false;
    },
    onCreate() {
      this.$router.push({ name: 'TasksAdd'}).catch(()=>{});
    },
    onEdit(item) {
      this.$router.push({ name: 'TasksEdit', params: { task_id: item.id }}).catch(()=>{});
    },
    onRead(item) {
      this.$router.push({ name: 'TasksView', params: { task_id: item.id }}).catch(()=>{});
    },
    onCopy(item) {
      this.$router.push({ name: 'TasksCopy', params: { task_id: item.id }}).catch(()=>{});
    },
    onRemove(row) {
      if (row.item) {
        this.deleteRow = row.item;
      }

      const title = "Удаление заявки";
      const message = `${this.$t("message.tasks.modal.removeDesc")} № ${this.deleteRow.id}?`;
      this.showModalDelete(title, message, async () => {
        const result = await this.deleteTask(this.deleteRow);

        if (result.error) {
          if (result.status === 422) {
            const message = this.$t('message.common.errorMessage');
            this.showError(message);
          }
          this.showToast('Ошибка при удалении.', 'Ошибка', 'danger');
        } else {
          this.showToast('Заявка удалена.', 'Успех', 'success');
          await this.getItems();
        }
      });
    },
    len(arr) {
      return arr ? arr.length : 0;
    },
    paidWorkers(row) {
      return this.yesOrNoPayIcon(row.item.paid_workers);
    },
    paid(row) {
      return this.yesOrNoText(row.item.paid);
    },
    // Filters
    removeFilterTag(tag) {
      if (tag.key === 'date') {
        this.filters.start_date = null;
        this.filters.end_date = null;
      }
      else if (tag.key === 'location') {
        this.filters.locations = [];
        this.$refs.location_multi_select.onClearSelected();
      }
      else if (tag.key === 'user') {
        this.filters.users = [];
        this.$refs.user_multi_select.onClearSelected();
      }
      else if (tag.key === 'status') {
        this.filters.status_id = 0;
      }
      else if (tag.key === 'paid') {
        this.filters.paid_workers = 0;
      }
      this.onSearch();
    },
    async onClearFilters() {
      const filters = this.getDefaultFilters();
      if (JSON.stringify(this.filters) === JSON.stringify(filters)) {
        return;
      }
      this.filters = filters;
      if (this.$refs.location_multi_select) {
        await this.$refs.location_multi_select.configure(this.filters.locations);
      }

      if (this.$refs.user_multi_select) {
        await this.$refs.user_multi_select.configure(this.filters.users);
      }
      await this.onSearch();
      this.updateSelected();
    },
    getDefaultFilters() {
      let filters = {
        query: '',
        start_date: null,
        end_date: null,
        status_id: 0,
        users: [],
        locations: [],
        paid_workers: 0
      }

      filters.start_date = this.getStartDateFilter();
      filters.end_date = null;
      return filters;
    },
    getStartDateFilter() {
      // TODO: Продумать как менять даты для роли бухгалтера через permissions
      if (this.hasPermission([PERM.STAFF])) {
        return this.getStartDateFilterAccountant();
      }

      let date = this.getNewDateToServer();
      date.setDate(date.getDate() - 60);
      return this.formatDate(date);
    },
    getStartDateFilterAccountant() {
      let date = this.getNewDateToServer();
      date.setDate(date.getDate() - 30);
      return this.formatDate(date);
    },
    onSelectLocation(locations) {
      this.filters.locations = locations.map(function (item) { return item.id; });
    },
    onSelectUser(users) {
      this.filters.users = users.map(function (item) { return item.id; });
    },
    // permissions
    isVisibleReadOnlyForRow(item) {
      if (this.hasPermission([PERM.TASK_EDIT_OTHER])) {
        return true;
      }

      if (this.hasPermission([PERM.TASK_EDIT])) {
        if (item.read_only) {
          return false;
        }
        return true;
      }

      return false;
    },
    canEditTasks() {
      return this.hasPermission([PERM.TASK_EDIT]);
    },
    // Export
    toggleTW() {
      this.isIncludeTW = !this.isIncludeTW;
    },
    async provideExportData() {
      const items = await this.getAllItems(this.isIncludeTW);

      if (!items || items.length === 0) {
        this.showSubmitError("Записей не найдено.");
        return [];
      }

      if (this.totalRows > 1000) {
        this.showSubmitError("Большое количество записей. Отфильтруйте данные, например, по периоду.");
        return [];
      }

      if (!this.isIncludeTW) {
        return items;
      }

      const allItems = [];
      items.forEach(task => {
        if (task.task_workers && task.task_workers.length > 0) {
          task.task_workers.forEach(tw => {
            allItems.push({ ...task, tw: tw });
          });
        } else {
          allItems.push(task);
        }
      });

      return allItems;
    },
    mapTaskForExport(item) {
      const tw = item.tw || null;

      return {
        id: item.id,
        customer_name: item.customer_name,
        start_address: item.start_address,
        location_name: item.location_name,
        start_date: this.formatTableDate(item.start_date),
        end_date: this.formatTableDate(item.end_date),
        total_worker_price_with_comiss: item.total_worker_price_with_comiss,
        total_price: item.total_price,
        payment_first: item.payment_first,
        executor_name: item.executor_name,
        author_name: item.author_name,
        create_date: this.formatTableDate(item.create_date),
        workers_count: item.workers_count,
        additional_cost: item.additional_cost,
        declarated_cost: item.declarated_cost,
        cargo_description: item.cargo_description,
        status_name: item.status_name,

        tw_worker_name: tw ? tw.worker_name : '',
        tw_worker_phone: tw ? tw.worker_phone : '',
        tw_worker_inn: tw && tw.worker ? tw.worker.inn : '',
        tw_worker_card: tw && tw.worker && tw.worker.payments_card && tw.worker.payments_card.card_number
          ? tw.worker.payments_card.card_number
          : '',
        tw_price: tw ? tw.price : '',
        tw_start_date: tw ? (tw.start_date ? this.formatTableDateTime(tw.start_date) : '') : '',
        tw_end_date: tw ? (tw.end_date ? this.formatTableDateTime(tw.end_date) : '') : '',
        tw_dinner: tw ? tw.dinner : '',
        tw_total_units: tw ? tw.total_units : '',
        tw_prepaid: tw ? tw.prepaid : '',
        tw_road_price: tw ? tw.road_price : '',
        tw_total_price: tw ? tw.total_price : '',
        tw_description: tw ? tw.description : '',
      };
    },
    async getAllItems(includeTW) {
      const locationIds = this.filters.locations.map(function (item) {
        return "&locations[]=" + item;
      });

      const usersIds = this.filters.users.map(function (item) {
        return "&executors[]=" + item;
      });

      const data = {
        page: 1,
        size: this.totalRows,
        query: this.filters.query,
        start_date: this.filters.start_date,
        end_date: this.filters.end_date,
        status_id: this.filters.status_id,
        paid_workers: this.filters.paid_workers,
        locations: locationIds.join(''),
        users: usersIds.join(''),
        sortBy: [this.sortBy],
        sortDesc: [this.sortDesc],
        include_tw: includeTW,
      }

      this.isBusyExport = true;
      const result = await this.getTasks(data);
      this.isBusyExport = false;

      if (result.data.tasks && result.data.tasks.length > 0) {
        return result.data.tasks;
      }

      return [];
    },
    showImportModal() {
      this.$refs.tasks_import.showModal();
    },
    handleTasksImport() {
      this.$bvToast.toast('Импорт заявок завершен.', {
        title: 'Импорт заявок',
        variant: 'success',
        autoHideDelay: 3000,
        solid: true
      });

      this.getItems();
    },
    onRowClicked(item) {
      if (this.isVisibleReadOnlyForRow(item)) {
        this.onEdit(item);
      } else {
        this.onRead(item);
      }
    },
    async selectAllRows(checked) {
      if (checked) {
        if (this.totalRows > 10000) {
          this.showSubmitError("Большое количество записей. Отфильтруйте данные, например, по периоду.");
          this.allSelected = false;
          return;
        }

        this.selected = await this.getAllItems(false);
        this.updateSelected();
      } else {
        this.selected = [];
        this.updateSelected();
      }
    },
    rowClicked(item) {
      // Ищем уже выбранные
      const existItem = this.selected.findIndex(e => e.id === item.id);

      if (existItem !== -1) {
        this.selected.splice(existItem, 1);
        this.$set(item, 'selected', false);
      } else {
        this.selected.push(item);
        this.$set(item, 'selected', true);
      }
    },
    updateSelected() {
      this.items.forEach((item) => {
        const isSelected = this.selected.some(e => e.id === item.id);
        this.$set(item, 'selected', isSelected);
      });

      this.selected = this.selected.map(item => {
        const updatedItem = this.items.find(i => i.id === item.id);
        return updatedItem ? updatedItem : item;
      });
    },
    tbodyRowClass(item) {
      return item && item.selected ? ["b-table-row-selected", "table-primary", "pointer-cell"] : ["pointer-cell"];
    },
    // Статус
    getStatusName(status_id) {
      const status = this.statuses.find(e => e.id === status_id)
      return status != null ? status.name : 'Ошибка';
    },
    getStatusStyle(status_id) {
      switch (status_id) {
        case 1:
          return 'primary';
        case 2:
          return 'info';
        case 3:
          return 'secondary';
        case 4:
          return 'success';
        default:
          return '';
      }
    },
    onInvoice() {
      this.$router.push({ name: 'InvoiceAdd', params: { tasks: this.selected }}).catch(()=>{});
    },
    async onNewStatus() {
      const taskIds = this.selected.map(obj => obj.id);
      const data = {
        task_ids: taskIds,
        status_id: 1,
      }
      await this.postTaskUpdateStatus(data);
      await this.getItems();
    },
    async onPayStatus() {
      const message = "Изменение приведет к закрытию заявки для редактирования.\nВы уверены что хотите продолжить?";
      const result = await this.showAcceptChangeStatus(message);
      if (!result) { return; }

      const taskIds = this.selected.map(obj => obj.id);
      const data = {
        task_ids: taskIds,
        status_id: 2,
      }
      await this.postTaskUpdateStatus(data);
      await this.getItems();
    },
    async onPayWorkers() {
      const message = "Изменение приведет к оплате всем исполнителям в заявке.\nВы уверены что хотите продолжить?";
      const result = await this.showAcceptChangeStatus(message);

      if (!result) { return; }

      const taskIds = this.selected.map(obj => obj.id);
      this.currentPayTaskData = {
        task_ids: taskIds,
      };
      this.$refs.modalSelectPayOrganization.show();
    },
    async showAcceptChangeStatus(message) {
      const title = "Изменение статуса";
      return await this.showAcceptModal(title, message);
    },
    updateHeights() {
      let topOffset = 167.8;
      let offset = `${topOffset}px`;
      let style = { maxHeight: `calc(100vh - ${offset}) !important`, contentVisibility: 'auto'}
      if (this.selected.length > 0) {
        topOffset = 232;
        offset = `${topOffset}px`;
        style = { maxHeight: `calc(100vh - ${offset}) !important`, contentVisibility: 'auto'}
      }
      this.tableStyle = style;
    },
    showInvoice(item) {
      const routeData = this.$router.resolve({name: 'InvoiceEdit', params: { invoice_id: item.invoice_id }});
      window.open(routeData.href, '_blank');
    },
    onInvoiceFile(item) {
      const id = item.invoice_id;
      this.exportInvoiceFile(id, "pdf", 1);
    },
    async exportInvoiceFile(id, exportType, hasStamp) {
      const data = { id, exportType, hasStamp }

      const result = await this.getInvoiceFile(data);

      if (result.error || !result.data) {
        this.showGenerateFileError("Ошибка создания файла", "Не удалось выполнить создание файла. Попробуйте повторить позднее.");
        return;
      }

      const fileName = this.getFileName(result.headers.get('content-disposition')).replace(/[+]/g,' ');

      this.handleFileDownload(result.data, fileName);
    },
    getFileName(disposition) {
      const defaultName = "untitled";

      if (!disposition) return defaultName;

      try {
        const { attachment, filename } = parse(disposition);
        return attachment ? filename : defaultName;
      } catch (e) {
        console.error(e);
        return defaultName;
      }
    },
    async exportTimesheetFile() {
      if (this.totalRows > 10000) {
        const message = "Большое количество записей. Отфильтруйте данные например по периоду";
        this.showError(message);
        return;
      }

      if (!this.dateValidation(this.filters.start_date)) {
        const message = "Выберите дату начала в фильтре";
        this.showError(message);
        return;
      }

      if (!this.dateValidation(this.filters.end_date)) {
        const message = "Выберите дату окончания в фильтре";
        this.showError(message);
        return;
      }

      const taskIds = this.selected.map(obj => obj.id);

      const locationIds = this.filters.locations.map(function (item) {
        return "&locations[]=" + item;
      });

      const usersIds = this.filters.users.map(function (item) {
        return "&executors[]=" + item;
      });

      const data = {
        status_id: this.filters.status_id,
        paid_workers: this.filters.paid_workers,
        start_date: this.filters.start_date,
        end_date: this.filters.end_date,
        locations: locationIds.join(''),
        users: usersIds.join(''),
        task_ids: taskIds
      }

      this.isBusyExport = true;
      const result = await this.postTaskTimesheetFile(data);

      if (!result || !result.data) {
        const title = "Ошибка создания файла";
        const message = "Не удалось выполнить создание файла. Попробуйте повторить позднее.";
        this.showGenerateFileError(title, message);
        this.isBusyExport = false;
        return;
      }

      this.isBusyExport = false;
      const fileName = this.getFileName(result.headers.get('content-disposition')).replace(/[+]/g,' ');

      this.handleFileDownload(result.data, fileName);
    },
    getUnit(unit_code) {
      const unit = this.getUnitByCode(unit_code);

      if (!unit) { return ""; }

      return unit.shortName || "";
    },
    onSelectOrg(org) {
      if (org === 0) {
        this.currentPayOrganization = null;
        return;
      }
      this.currentPayOrganization = org;
    },
    async onPay() {
      if (this.currentPayTaskData == null) {
        const message = "Не данных для оплаты";
        this.showError(message);
        return;
      }

      if (this.currentPayOrganization == null) {
        const message = "Не выбрана организация для оплаты";
        this.showError(message);
        return;
      }

      await this.postTaskUpdatePay(this.currentPayTaskData, this.currentPayOrganization);

      this.currentPayOrganization = null;
      this.currentPayTaskData = null;

      await this.getItems();
    }
  },
  watch: {
    selected(newValue, oldValue) {
      if (newValue.length === 0) {
        this.indeterminate = false;
        this.allSelected = false;
      } else if (newValue.length === this.totalRows) {
        this.indeterminate = false;
        this.allSelected = true;
      } else {
        this.indeterminate = true;
        this.allSelected = false;
      }

      this.updateHeights();
    },
    totalRows(newValue, oldValue) {
      if (newValue === 0) {
        this.allSelectDisabled = true;
      } else if (newValue > 10000) {
        this.allSelectDisabled = true;
      } else {
        this.allSelectDisabled = false;
      }
    },
  },
}
</script>

<style>
#tasks td {
  vertical-align: middle;
}

.tasksSelect {
  min-width: 50px;
  text-align: center;
}

.tasksId {
  min-width: 50px;
  text-align: center;
}

.tasksLocation {
  min-width: 200px;
  max-width: 250px;
}

.tasksDate {
  min-width: 130px;
}

.tasksWorkers {
  min-width: 100px;
}

.tasksTotalHours {
  min-width: 100px;
}

.tasksTotalPrice {
  min-width: 120px;
}

.tasksTotalWorkerPrice {
  min-width: 150px;
}

.tasksStatus {
  min-width: 220px;
}

.tasksInvoice {
  min-width: 70px;
  text-align: center;
}

.tasksExecutor {
  min-width: 200px;
  max-width: 200px;
  white-space: nowrap;
  overflow-x: hidden;
  text-overflow: ellipsis;
}

.tasksAction {
  min-width: 80px;
  padding-right: 10px !important;
  text-align: center;
}

.export-button {
  margin-top: 4px;
}
</style>



// WEBPACK FOOTER //
// src/components/Tasks/Tasks.vue